/**
 * iOS Audio Routing Helper
 *
 * 解决 iOS Safari 在 getUserMedia 录音后音频输出切换到听筒（Receiver）且无法自动切回扬声器（Speaker）的问题。
 *
 * 根本原因：
 *   iOS 调用 getUserMedia 后，系统将 AVAudioSession 切换为 PlayAndRecord 模式（默认路由到听筒）。
 *   必须先停止所有麦克风 stream tracks，再通知系统切回扬声器，否则系统会忽略切换请求。
 *
 * 正确调用顺序（调用方负责）：
 *   1. audioRecorder.stopStream()        —— 先停止麦克风硬件占用
 *   2. IOSAudioHelper.restoreAudioOutput() —— 再恢复扬声器输出
 *
 * 双重保障机制：
 *   A. navigator.audioSession.type = 'playback'（iOS 16.4+）
 *   B. 用 Web Audio API 播放一段极短的 440Hz 音调，强制 iOS 重新评估音频路由
 *      （适用于不支持 audioSession API 的 iOS 版本）
 */

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const IOSAudioHelper = {

    /**
     * 恢复音频输出到扬声器。
     * 重要：调用方必须已经调用 stream.getTracks().forEach(t => t.stop()) 停止了所有麦克风轨道，
     * 否则 iOS 会忽略 audioSession 类型切换，本方法无效。
     */
    restoreAudioOutput: function() {
        if (!isIOS) return;

        console.log('IOSAudioHelper: restoreAudioOutput 开始');

        try {
            // A. iOS 16.4+ 的 Web Audio Session API
            if (navigator.audioSession) {
                navigator.audioSession.type = 'playback';
                console.log('IOSAudioHelper: audioSession.type = playback');
            }

            // B. 播放一段极短的音调，强制 iOS 将路由切回扬声器
            // 这对 iOS < 16.4 及 audioSession API 不生效的情况是必要的保障
            // 必须立即执行（不能有 setTimeout），确保在下一段课程音频播放前完成路由切换
            this._playToneToForceSpeaker();

            // C. 延迟后将 audioSession 重置为 auto，让系统接管后续路由决策
            setTimeout(() => {
                try {
                    if (navigator.audioSession) {
                        navigator.audioSession.type = 'auto';
                        console.log('IOSAudioHelper: audioSession.type = auto');
                    }
                } catch(e) {}
            }, 700);

        } catch(e) {
            console.warn('IOSAudioHelper: restoreAudioOutput error', e);
        }
    },

    /**
     * 通过播放一段极短的 440Hz 音调，强制 iOS 将音频路由切回扬声器。
     * 原理：当所有麦克风轨道已停止后，iOS 在一个新的 AudioContext 开始播放时，
     * 会重新评估音频会话类型，并默认选择扬声器输出。
     */
    _playToneToForceSpeaker: function() {
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) return;

            const ctx = new AudioCtx();

            // 如果上下文被挂起（浏览器自动暂停策略），先恢复它
            const doPlay = () => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();

                osc.frequency.value = 440; // A4 标准音，音调清晰，iOS 路由切换效果好

                // 音量极小（0.01），对用户几乎无感，但足以触发 iOS 硬件路由切换
                gain.gain.setValueAtTime(0.01, ctx.currentTime);

                osc.connect(gain);
                gain.connect(ctx.destination);

                osc.start(ctx.currentTime);
                osc.stop(ctx.currentTime + 0.3); // 300ms 足够触发路由切换

                osc.onended = () => {
                    try { ctx.close(); } catch(e) {}
                    console.log('IOSAudioHelper: 音调播放完毕，路由应已切回扬声器');
                };
            };

            if (ctx.state === 'suspended') {
                ctx.resume().then(doPlay).catch(e => {
                    console.warn('IOSAudioHelper: AudioContext resume failed', e);
                });
            } else {
                doPlay();
            }

        } catch(e) {
            console.warn('IOSAudioHelper: _playToneToForceSpeaker error', e);
        }
    }
};

export default IOSAudioHelper;
